// ---------------------------------------------------------------------------
// narrative.js — optional LLM narrative layer on top of the deterministic
// caseload analytics in ./analytics.js.
//
// Everything the AI Insights page needs to render already exists as a
// structured JS object from analyzeCaseload(). The functions here call
// the shared Supabase Edge Function (`draft-goal`, which handles multiple
// prompt kinds) to turn that structured object into:
//
//   - analyze_student       → a short plain-English narrative summarizing
//                             a single student's trajectory + behavior.
//   - suggest_interventions → a bulleted list of PBIS-family interventions
//                             appropriate for a specific function of
//                             behavior + antecedent profile.
//   - parent_email          → a paragraph the teacher can paste into an
//                             email to a caregiver, framing progress in
//                             encouraging but honest language.
//
// The client passes only summary numbers, enum labels, and short public
// fields — grade, primary disability category, aggregate counts — not
// raw incident notes, not medical info, not free-text behavior
// descriptions. The student's initials are the only identifier, so the
// LLM has enough context to write meaningfully without seeing PII.
//
// Failure model
// -------------
//
//   * If Supabase isn't configured → immediately throw an error the UI
//     can render, and the page falls back to showing the deterministic
//     analytics alone.
//   * If the Edge Function responds 501 (no provider key set), we surface
//     "AI narrative isn't set up on this project yet — the numbers above
//     are still live." No BYO fallback here because these prompts are
//     server-only (the BYO path in goalAi.js only knows goal-drafting
//     prompts, and re-adding the SMART-goal system prompt everywhere
//     would be error-prone).
//   * Any 4xx/5xx surfaces the server's message verbatim.
// ---------------------------------------------------------------------------

import { supabase, isSupabaseConfigured } from '../supabase';

const EDGE_FUNCTION_NAME =
  (typeof import.meta !== 'undefined' &&
    import.meta.env &&
    typeof import.meta.env.VITE_AI_FUNCTION_NAME === 'string' &&
    import.meta.env.VITE_AI_FUNCTION_NAME.trim()) ||
  'draft-goal';

export function isNarrativeAvailable() {
  return isSupabaseConfigured() && supabase !== null;
}

// initialsFor — safe display id for LLM prompts. Never sends the actual
// name off the device; the LLM sees "M.J." rather than "Marcus Johnson."
export function initialsFor(fullName) {
  if (!fullName || typeof fullName !== 'string') return 'Student';
  const parts = fullName.trim().split(/\s+/);
  if (parts.length === 0) return 'Student';
  return parts
    .slice(0, 2)
    .map((p) => (p[0] || '').toUpperCase() + '.')
    .join('');
}

// ---------- Payload builders --------------------------------------------

// Take a full studentInsight (from analyzeCaseload) and shrink it to the
// minimum payload the LLM needs. Enum labels get resolved to human-readable
// text upstream, so this function just picks fields.
export function buildAnalyzeStudentPayload(studentInsight) {
  if (!studentInsight) return null;
  return {
    displayId: initialsFor(studentInsight.studentName),
    grade: studentInsight.grade || null,
    primaryDisability: studentInsight.primaryDisability || null,
    reviewDaysLeft: studentInsight.review?.daysLeft ?? null,
    reviewOverdue: Boolean(studentInsight.review?.overdue),
    activeGoalCount: studentInsight.activeGoalCount,
    masteredGoalCount: studentInsight.masteredGoalCount,
    goals: (studentInsight.goals || []).map((g) => ({
      area: g.area,
      latest: g.latest != null ? Math.round(g.latest) : null,
      slopePerWeek: g.slopePerWeek != null ? Number(g.slopePerWeek.toFixed(2)) : null,
      staleDays: g.staleDays,
      trend: g.trend,
      projectionDays: g.projectionDays,
      entryCount: g.entryCount,
    })),
    behavior: {
      total: studentInsight.behavior.total,
      thisWeek: studentInsight.behavior.thisWeek,
      lastWeek: studentInsight.behavior.lastWeek,
      avgIntensity: studentInsight.behavior.avgIntensity != null
        ? Number(studentInsight.behavior.avgIntensity.toFixed(1))
        : null,
      topDayOfWeek: studentInsight.behavior.topDayOfWeek?.label || null,
      topLocation: studentInsight.behavior.topLocation?.label || null,
      topAntecedent: studentInsight.behavior.topAntecedent?.label || null,
      topTimeBucket: studentInsight.behavior.topTimeBucket?.label || null,
      likelyFunction: studentInsight.behavior.likelyFunction?.value || 'unknown',
      functionSource: studentInsight.behavior.likelyFunction?.source || 'inferred',
      functionConfidence: studentInsight.behavior.likelyFunction?.confidence || 'low',
      patternHeadline: studentInsight.behavior.patternHeadline || null,
    },
    flags: studentInsight.flags,
    signal: studentInsight.signal,
  };
}

export function buildSuggestInterventionsPayload(studentInsight) {
  const base = buildAnalyzeStudentPayload(studentInsight);
  if (!base) return null;
  return {
    displayId: base.displayId,
    grade: base.grade,
    primaryDisability: base.primaryDisability,
    // Behavior-only — we don't need the goals column for intervention
    // suggestions, and less context = cheaper + faster.
    behavior: base.behavior,
    antecedentBreakdown: (studentInsight.behavior.byAntecedent || []).slice(0, 5).map((a) => ({
      antecedent: a.label,
      count: a.count,
    })),
    locationBreakdown: (studentInsight.behavior.byLocation || []).slice(0, 5).map((a) => ({
      location: a.label,
      count: a.count,
    })),
  };
}

export function buildParentEmailPayload(studentInsight) {
  const base = buildAnalyzeStudentPayload(studentInsight);
  if (!base) return null;
  return {
    displayId: base.displayId,
    grade: base.grade,
    goals: base.goals,
    behaviorSummary: {
      thisWeek: base.behavior.thisWeek,
      lastWeek: base.behavior.lastWeek,
      avgIntensity: base.behavior.avgIntensity,
      likelyFunction: base.behavior.likelyFunction,
    },
    positive: (studentInsight.goals || [])
      .filter((g) => g.isAhead || g.trend === 'improving')
      .map((g) => ({ area: g.area, latest: g.latest != null ? Math.round(g.latest) : null })),
  };
}

// ---------- Edge function invocation ------------------------------------

async function invokeEdge(body) {
  if (!isNarrativeAvailable()) {
    const err = new Error(
      'AI narrative isn\'t available in this build — Supabase isn\'t configured. Everything above is still live from your own data.',
    );
    err.__unavailable = true;
    throw err;
  }

  const { data, error } = await supabase.functions.invoke(EDGE_FUNCTION_NAME, { body });
  if (error) {
    const status = error?.status ?? error?.context?.status;
    let message = error?.message || 'AI narrative failed.';
    try {
      const ctx = error?.context;
      if (ctx && typeof ctx.json === 'function') {
        const parsed = await ctx.json();
        if (parsed?.error) message = parsed.error;
      } else if (ctx?.body && typeof ctx.body === 'string') {
        try {
          const parsed = JSON.parse(ctx.body);
          if (parsed?.error) message = parsed.error;
        } catch {
          message = ctx.body || message;
        }
      }
    } catch {
      // fall through with generic message
    }
    if (status === 501 || status === 404) {
      const err = new Error(message);
      err.__unavailable = true;
      err.status = status;
      throw err;
    }
    const err = new Error(message);
    err.status = status;
    throw err;
  }

  if (!data || typeof data.text !== 'string') {
    throw new Error('AI narrative returned an empty response. Try again.');
  }
  return data.text.trim();
}

// ---------- Public helpers ----------------------------------------------

export async function analyzeStudentNarrative(studentInsight) {
  const payload = buildAnalyzeStudentPayload(studentInsight);
  if (!payload) throw new Error('No student data to summarize.');
  return invokeEdge({ kind: 'analyze_student', payload });
}

export async function suggestInterventionsNarrative(studentInsight) {
  const payload = buildSuggestInterventionsPayload(studentInsight);
  if (!payload) throw new Error('No behavior data to build interventions from.');
  return invokeEdge({ kind: 'suggest_interventions', payload });
}

export async function draftParentEmail(studentInsight, parentName = '') {
  const payload = buildParentEmailPayload(studentInsight);
  if (!payload) throw new Error('No student data to build a parent email from.');
  return invokeEdge({
    kind: 'parent_email',
    payload: { ...payload, parentName: (parentName || '').trim() || null },
  });
}
